// useDeviceListFiltering – filters state, filter predicate, filteredRows, allVisibleIds.

import { useEffect, useMemo, useState } from 'react';
import type {
  DeviceMasterRow,
  DeviceFiltersValue,
  DeviceStatus,
} from '../../../../types/DeviceListTypes';
import { loadFilters, saveFilters } from './deviceListStorage';
import type { CommandDdbItem } from '../../../../types/Commands';
import { isInServiceLifecycle, normalizeLifecycleSnapshot } from '../../../helpers/deviceLifecycle';
import {
  isFrontDeviceId,
  isRearDeviceId,
  isRaceModeActive,
  pairedFrontIdFromRear,
  pairedRearIdFromFront,
} from '../../../helpers/devicePairing';

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const earthRadiusKm = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

import type { UseDeviceListFilteringParams, UseDeviceListFilteringResult } from '../types/DeviceListViewTypes';

export type { UseDeviceListFilteringParams, UseDeviceListFilteringResult };

export function useDeviceListFiltering({
  sortedRows,
  searchQuery,
  columns,
  csvFilterIds,
  eventDeviceFilterIds,
  activeRequestId,
  requestFilterIds,
  idKey,
  statusMap,
  tlsMap,
  displayedLatestCommandMap,
  onlineMap,
  busyMap = {},
  lifecycleFilterApplies = false,
  lifecycleMap = {},
  externalRadiusFilter = null,
  externalDeviceIdsFilter = null,
}: UseDeviceListFilteringParams): UseDeviceListFilteringResult {
  const [filters, setFilters] = useState<DeviceFiltersValue>(() => loadFilters());

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const expandWithPairs = (ids: string[] | null): string[] | null => {
    if (!Array.isArray(ids)) return ids;
    const expanded = new Set<string>();
    for (const raw of ids) {
      const id = String(raw ?? '').trim();
      if (!id) continue;
      expanded.add(id);
      const paired = pairedFrontIdFromRear(id) ?? pairedRearIdFromFront(id);
      if (paired) expanded.add(paired);
    }
    return Array.from(expanded);
  };

  const filteredRows = useMemo(() => {
    let result = sortedRows;

    if (searchQuery.trim() && columns.length) {
      const q = searchQuery.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const val = row[col];
          if (val === undefined || val === null) return false;
          return String(val).toLowerCase().includes(q);
        }),
      );
    }

    if (csvFilterIds && csvFilterIds.length && idKey) {
      const allowed = new Set(csvFilterIds.map((id) => id.trim()).filter((id) => id.length > 0));
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    if (eventDeviceFilterIds && idKey) {
      const allowed = new Set(
        eventDeviceFilterIds.map((id) => id.trim()).filter((id) => id.length > 0),
      );
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    const effectiveRequestFilterIds = activeRequestId ? expandWithPairs(requestFilterIds) : null;
    if (effectiveRequestFilterIds && idKey) {
      const allowed = new Set(
        effectiveRequestFilterIds.map((x) => x.trim()).filter((x) => x.length > 0),
      );
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    if (externalDeviceIdsFilter && idKey) {
      const allowed = new Set(
        externalDeviceIdsFilter
          .map((x) => x.trim())
          .filter((x) => x.length > 0),
      );
      if (allowed.size > 0) {
        result = result.filter((row) => {
          const rowId = row[idKey];
          return typeof rowId === 'string' && allowed.has(rowId);
        });
      }
    }

    result = result.filter((row) => {
      if (!idKey) return true;

      const deviceId = row[idKey] as string | undefined;
      if (!deviceId) return false;

      const status = statusMap[deviceId];
      const latestCmd = displayedLatestCommandMap[deviceId];
      const tls = Object.prototype.hasOwnProperty.call(tlsMap, deviceId) && tlsMap[deviceId];
      const isOnline = !!onlineMap[deviceId];

      if (filters.online === 'online' && !isOnline) return false;
      if (filters.online === 'offline' && isOnline) return false;

      if (filters.tls === 'provisioned' && tls !== true) return false;
      if (filters.tls === 'unprovisioned' && tls !== false) return false;

      if (lifecycleFilterApplies && filters.lifecycle !== 'any') {
        const row = deviceId ? lifecycleMap[deviceId] : undefined;
        const snap = normalizeLifecycleSnapshot(row);

        if (filters.lifecycle === 'active' && !isInServiceLifecycle(snap)) return false;
        if (filters.lifecycle === 'lost' && !snap.lost) return false;
        if (filters.lifecycle === 'decommissioned' && !snap.decommissioned) return false;
        if (filters.lifecycle === 'damaged' && !snap.damaged) return false;
        if (filters.lifecycle === 'errored' && !snap.errored) return false;
      }

      if (filters.mode !== 'any') {
        const chargingVal =
          typeof (status as Record<string, unknown>)?.charging === 'number'
            ? (status as Record<string, unknown>).charging
            : undefined;
        const busyFlag =
          typeof (status as Record<string, unknown>)?.busy === 'number'
            ? Number((status as Record<string, unknown>).busy)
            : 0;
        const isBusyMirrored = !!busyMap[deviceId] || busyFlag > 0;
        const raceMode = isRaceModeActive(status);
        const puckCharging = typeof status?.puck === 'number' && status.puck > 0;
        const isCharging = puckCharging || chargingVal === 1;

        let derivedMode: DeviceFiltersValue['mode'] = 'app';
        if (raceMode) derivedMode = 'race';
        else if (!isOnline) derivedMode = 'offline';
        else if (isBusyMirrored) derivedMode = 'busy';
        else if (isCharging) derivedMode = 'charging';

        if (derivedMode !== filters.mode) return false;
      }

      if (filters.deviceRole !== 'any') {
        if (filters.deviceRole === 'rear'  && !isRearDeviceId(deviceId))  return false;
        if (filters.deviceRole === 'front' && !isFrontDeviceId(deviceId)) return false;
      }

      if (filters.frontLink !== 'any') {
        const linkStatus = typeof status?.front_link_status === 'string'
          ? status.front_link_status.trim()
          : '';
        if (filters.frontLink === 'linked'   && !linkStatus) return false;
        if (filters.frontLink === 'unlinked' && !!linkStatus) return false;
      }

      const battMv = typeof status?.batt_mv === 'number' ? status.batt_mv : undefined;
      if (filters.battMvMin != null && battMv != null && battMv < filters.battMvMin) return false;
      if (filters.battMvMax != null && battMv != null && battMv > filters.battMvMax) return false;

      const raceSize = typeof status?.race_log_size === 'number' ? status.race_log_size : undefined;
      if (filters.raceLogMin != null && raceSize != null && raceSize < filters.raceLogMin)
        return false;
      if (filters.raceLogMax != null && raceSize != null && raceSize > filters.raceLogMax)
        return false;

      const debugSize =
        typeof status?.debug_log_size === 'number' ? status.debug_log_size : undefined;
      if (filters.debugLogMin != null && debugSize != null && debugSize < filters.debugLogMin)
        return false;
      if (filters.debugLogMax != null && debugSize != null && debugSize > filters.debugLogMax)
        return false;

      if (filters.lastHeardFrom || filters.lastHeardTo) {
        const updatedAtSec = typeof status?.updatedAt === 'number' ? status.updatedAt : 0;
        if (!updatedAtSec) return false;
        const updatedMs = updatedAtSec > 1e12 ? updatedAtSec : updatedAtSec * 1000;
        if (filters.lastHeardFrom) {
          const fromMs = Date.parse(filters.lastHeardFrom);
          if (!Number.isNaN(fromMs) && updatedMs < fromMs) return false;
        }
        if (filters.lastHeardTo) {
          const toMs = Date.parse(filters.lastHeardTo);
          if (!Number.isNaN(toMs) && updatedMs > toMs) return false;
        }
      }

      if (filters.lastCmdType !== 'any') {
        if (!latestCmd || latestCmd.type !== filters.lastCmdType) return false;
      }

      if (filters.lastCmdStatuses && filters.lastCmdStatuses.length > 0) {
        const statusUpper = (latestCmd?.status || '').toUpperCase();
        const selected = new Set(filters.lastCmdStatuses);
        if (!selected.has(statusUpper as (typeof filters.lastCmdStatuses)[number])) return false;
      }

      const targetContains = (filters.lastCmdTargetContains ?? '').trim();
      if (targetContains.length > 0) {
        const needle = targetContains.toLowerCase();
        const target = (latestCmd?.targetVersion || '').toLowerCase();
        if (!target.includes(needle)) return false;
      }

      if (filters.lastCmdUpdatedFrom || filters.lastCmdUpdatedTo) {
        if (!latestCmd || typeof latestCmd.updatedAt !== 'number') return false;
        const updatedSec = latestCmd.updatedAt;
        const updatedMs = updatedSec > 1e12 ? updatedSec : updatedSec * 1000;
        if (filters.lastCmdUpdatedFrom) {
          const fromMs = Date.parse(filters.lastCmdUpdatedFrom);
          if (!Number.isNaN(fromMs) && updatedMs < fromMs) return false;
        }
        if (filters.lastCmdUpdatedTo) {
          const toMs = Date.parse(filters.lastCmdUpdatedTo);
          if (!Number.isNaN(toMs) && updatedMs > toMs) return false;
        }
      }

      const hasUserLocationRadiusFilter =
        filters.locationCenterLat != null
        && filters.locationCenterLng != null
        && filters.locationRadiusKm != null;
      const hasExternalRadiusFilter =
        externalRadiusFilter != null
        && Number.isFinite(externalRadiusFilter.centerLat)
        && Number.isFinite(externalRadiusFilter.centerLng)
        && Number.isFinite(externalRadiusFilter.radiusKm);
      if (hasUserLocationRadiusFilter || hasExternalRadiusFilter) {
        const centerLat = hasExternalRadiusFilter
          ? Number(externalRadiusFilter?.centerLat)
          : (filters.locationCenterLat as number);
        const centerLng = hasExternalRadiusFilter
          ? Number(externalRadiusFilter?.centerLng)
          : (filters.locationCenterLng as number);
        const radiusKm = hasExternalRadiusFilter
          ? Number(externalRadiusFilter?.radiusKm)
          : (filters.locationRadiusKm as number);

        const centerValid =
          Number.isFinite(centerLat)
          && Number.isFinite(centerLng)
          && centerLat >= -90
          && centerLat <= 90
          && centerLng >= -180
          && centerLng <= 180
          && Number.isFinite(radiusKm)
          && radiusKm >= 0;

        if (!centerValid) return false;

        const deviceLat = typeof status?.lat === 'number' ? status.lat : null;
        const deviceLng = typeof status?.lng === 'number' ? status.lng : null;
        if (deviceLat == null || deviceLng == null) return false;
        if (!Number.isFinite(deviceLat) || !Number.isFinite(deviceLng)) return false;
        if (Math.abs(deviceLat) > 90 || Math.abs(deviceLng) > 180) return false;

        const distanceKm = haversineKm(centerLat, centerLng, deviceLat, deviceLng);
        if (distanceKm > radiusKm) return false;
      }

      return true;
    });

    return result;
  }, [
    sortedRows,
    searchQuery,
    columns,
    csvFilterIds,
    eventDeviceFilterIds,
    activeRequestId,
    requestFilterIds,
    idKey,
    statusMap,
    tlsMap,
    filters,
    displayedLatestCommandMap,
    onlineMap,
    busyMap,
    lifecycleFilterApplies,
    lifecycleMap,
    externalRadiusFilter,
    externalDeviceIdsFilter,
  ]);

  const allVisibleIds = useMemo(() => {
    if (!idKey) return [] as string[];
    return filteredRows
      .map((row) => row[idKey])
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }, [filteredRows, idKey]);

  const allVisibleIdsBeforeLocationFilter = useMemo(() => {
    let result = sortedRows;

    if (searchQuery.trim() && columns.length) {
      const q = searchQuery.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const val = row[col];
          if (val === undefined || val === null) return false;
          return String(val).toLowerCase().includes(q);
        }),
      );
    }

    if (csvFilterIds && csvFilterIds.length && idKey) {
      const allowed = new Set(csvFilterIds.map((id) => id.trim()).filter((id) => id.length > 0));
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    if (eventDeviceFilterIds && idKey) {
      const allowed = new Set(
        eventDeviceFilterIds.map((id) => id.trim()).filter((id) => id.length > 0),
      );
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    const effectiveRequestFilterIds = activeRequestId ? expandWithPairs(requestFilterIds) : null;
    if (effectiveRequestFilterIds && idKey) {
      const allowed = new Set(
        effectiveRequestFilterIds.map((x) => x.trim()).filter((x) => x.length > 0),
      );
      result = result.filter((row) => {
        const rowId = row[idKey];
        return typeof rowId === 'string' && allowed.has(rowId);
      });
    }

    if (externalDeviceIdsFilter && idKey) {
      const allowed = new Set(
        externalDeviceIdsFilter
          .map((x) => x.trim())
          .filter((x) => x.length > 0),
      );
      if (allowed.size > 0) {
        result = result.filter((row) => {
          const rowId = row[idKey];
          return typeof rowId === 'string' && allowed.has(rowId);
        });
      }
    }

    result = result.filter((row) => {
      if (!idKey) return true;

      const deviceId = row[idKey] as string | undefined;
      if (!deviceId) return false;

      const status = statusMap[deviceId];
      const latestCmd = displayedLatestCommandMap[deviceId];
      const tls = Object.prototype.hasOwnProperty.call(tlsMap, deviceId) && tlsMap[deviceId];
      const isOnline = !!onlineMap[deviceId];

      if (filters.online === 'online' && !isOnline) return false;
      if (filters.online === 'offline' && isOnline) return false;

      if (filters.tls === 'provisioned' && tls !== true) return false;
      if (filters.tls === 'unprovisioned' && tls !== false) return false;

      if (lifecycleFilterApplies && filters.lifecycle !== 'any') {
        const row2 = deviceId ? lifecycleMap[deviceId] : undefined;
        const snap = normalizeLifecycleSnapshot(row2);

        if (filters.lifecycle === 'active' && !isInServiceLifecycle(snap)) return false;
        if (filters.lifecycle === 'lost' && !snap.lost) return false;
        if (filters.lifecycle === 'decommissioned' && !snap.decommissioned) return false;
        if (filters.lifecycle === 'damaged' && !snap.damaged) return false;
        if (filters.lifecycle === 'errored' && !snap.errored) return false;
      }

      if (filters.mode !== 'any') {
        const chargingVal =
          typeof (status as Record<string, unknown>)?.charging === 'number'
            ? (status as Record<string, unknown>).charging
            : undefined;
        const busyFlag =
          typeof (status as Record<string, unknown>)?.busy === 'number'
            ? Number((status as Record<string, unknown>).busy)
            : 0;
        const isBusyMirrored = !!busyMap[deviceId] || busyFlag > 0;
        const raceMode = isRaceModeActive(status);
        const puckCharging = typeof status?.puck === 'number' && status.puck > 0;
        const isCharging = puckCharging || chargingVal === 1;

        let derivedMode: DeviceFiltersValue['mode'] = 'app';
        if (raceMode) derivedMode = 'race';
        else if (!isOnline) derivedMode = 'offline';
        else if (isBusyMirrored) derivedMode = 'busy';
        else if (isCharging) derivedMode = 'charging';

        if (derivedMode !== filters.mode) return false;
      }

      if (filters.deviceRole !== 'any') {
        if (filters.deviceRole === 'rear'  && !isRearDeviceId(deviceId))  return false;
        if (filters.deviceRole === 'front' && !isFrontDeviceId(deviceId)) return false;
      }

      if (filters.frontLink !== 'any') {
        const linkStatus = typeof status?.front_link_status === 'string'
          ? status.front_link_status.trim()
          : '';
        if (filters.frontLink === 'linked'   && !linkStatus) return false;
        if (filters.frontLink === 'unlinked' && !!linkStatus) return false;
      }

      const battMv = typeof status?.batt_mv === 'number' ? status.batt_mv : undefined;
      if (filters.battMvMin != null && battMv != null && battMv < filters.battMvMin) return false;
      if (filters.battMvMax != null && battMv != null && battMv > filters.battMvMax) return false;

      const raceSize = typeof status?.race_log_size === 'number' ? status.race_log_size : undefined;
      if (filters.raceLogMin != null && raceSize != null && raceSize < filters.raceLogMin)
        return false;
      if (filters.raceLogMax != null && raceSize != null && raceSize > filters.raceLogMax)
        return false;

      const debugSize =
        typeof status?.debug_log_size === 'number' ? status.debug_log_size : undefined;
      if (filters.debugLogMin != null && debugSize != null && debugSize < filters.debugLogMin)
        return false;
      if (filters.debugLogMax != null && debugSize != null && debugSize > filters.debugLogMax)
        return false;

      if (filters.lastHeardFrom || filters.lastHeardTo) {
        const updatedAtSec = typeof status?.updatedAt === 'number' ? status.updatedAt : 0;
        if (!updatedAtSec) return false;
        const updatedMs = updatedAtSec > 1e12 ? updatedAtSec : updatedAtSec * 1000;
        if (filters.lastHeardFrom) {
          const fromMs = Date.parse(filters.lastHeardFrom);
          if (!Number.isNaN(fromMs) && updatedMs < fromMs) return false;
        }
        if (filters.lastHeardTo) {
          const toMs = Date.parse(filters.lastHeardTo);
          if (!Number.isNaN(toMs) && updatedMs > toMs) return false;
        }
      }

      if (filters.lastCmdType !== 'any') {
        if (!latestCmd || latestCmd.type !== filters.lastCmdType) return false;
      }

      if (filters.lastCmdStatuses && filters.lastCmdStatuses.length > 0) {
        const statusUpper = (latestCmd?.status || '').toUpperCase();
        const selected = new Set(filters.lastCmdStatuses);
        if (!selected.has(statusUpper as (typeof filters.lastCmdStatuses)[number])) return false;
      }

      const targetContains = (filters.lastCmdTargetContains ?? '').trim();
      if (targetContains.length > 0) {
        const needle = targetContains.toLowerCase();
        const target = (latestCmd?.targetVersion || '').toLowerCase();
        if (!target.includes(needle)) return false;
      }

      if (filters.lastCmdUpdatedFrom || filters.lastCmdUpdatedTo) {
        if (!latestCmd || typeof latestCmd.updatedAt !== 'number') return false;
        const updatedSec = latestCmd.updatedAt;
        const updatedMs = updatedSec > 1e12 ? updatedSec : updatedSec * 1000;
        if (filters.lastCmdUpdatedFrom) {
          const fromMs = Date.parse(filters.lastCmdUpdatedFrom);
          if (!Number.isNaN(fromMs) && updatedMs < fromMs) return false;
        }
        if (filters.lastCmdUpdatedTo) {
          const toMs = Date.parse(filters.lastCmdUpdatedTo);
          if (!Number.isNaN(toMs) && updatedMs > toMs) return false;
        }
      }

      return true;
    });

    if (!idKey) return [] as string[];
    return result
      .map((row) => row[idKey])
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }, [
    sortedRows,
    searchQuery,
    columns,
    csvFilterIds,
    eventDeviceFilterIds,
    activeRequestId,
    requestFilterIds,
    idKey,
    statusMap,
    tlsMap,
    filters,
    displayedLatestCommandMap,
    onlineMap,
    busyMap,
    lifecycleFilterApplies,
    lifecycleMap,
    externalDeviceIdsFilter,
  ]);

  return { filters, setFilters, filteredRows, allVisibleIds, allVisibleIdsBeforeLocationFilter };
}
